import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import localforage from "localforage";

import Grid from "../../Components/Grid/Grid";
import Modal from "../../Components/Modal/Incident/Modal";
import Button from "../../Components/button/button";
import LoadingSpinner from "../../Components/Loading/LoadingSpinner";

import "../../Style/IncidentView.css";
import config from "../../config";
import getColumnDefs from "../../utils/Incident/incidentColumnDefs";
import { IncidentData } from "../../utils/Incident/incidentTypes";
import { formatDuration } from "../../utils/durationUtils";
import { handleExcelUpload } from "../../utils/Incident/handleExcelUpload";
import { authFetch } from "../../utils/Security/authFetch";
import { GridApi } from "ag-grid-community";

interface Page<T> {
  content: T[];
  pageable: any;
  totalElements: number;
  totalPages: number;
  last: boolean;
  number: number;
  size: number;
  first: boolean;
  numberOfElements: number;
  sort: any;
  empty: boolean;
}
type CacheData = {
  rows: IncidentData[];
  ts: number;
} | null;
const IncidentView: React.FC = () => {
  const isForTelebras = localStorage.getItem("isForTelebras") === "true";
  const isForNilesat = localStorage.getItem("isForNilesat") === "true";

  const userId = localStorage.getItem("userid");
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const CACHE_KEY = `incidents_${isAdmin ? "admin" : userId}`;
  const FILTER_KEY = `${CACHE_KEY}_filters`;
  const [viewMode, setViewMode] = useState<"all" | "my" | "group">("all");
  const [rowData, setRowData] = useState<IncidentData[]>([]);
  const [filteredData, setFilteredData] = useState<IncidentData[]>([]);
  const [selectedIncident, setSelectedIncident] = useState<IncidentData | null>(
      null
  );
  const username = localStorage.getItem("username") || "System";
  const userTeam = localStorage.getItem("position");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const isForPetrobras = localStorage.getItem("isForPetrobras") === "true";

  const gridApiRef = useRef<GridApi | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const applySearchFilter = (item: IncidentData) => {
    const query = searchQuery.toLowerCase();
    return Object.values(item).some(
        (value) => value != null && value.toString().toLowerCase().includes(query)
    );
  };
  // ✅ MY TODO
  const loadMyTodo = async () => {
    if (!username) return;

    setViewMode("my"); // ⭐ important
    setLoading(true);

    try {
      const res = await authFetch(
          `${config.apiBaseUrl}/api/incidents/my-todo?username=${encodeURIComponent(username)}`,
      );

      const data: IncidentData[] = await res.json();

      setRowData(data || []);
      setFilteredData(data || []);
    } catch (e) {
      console.error("Failed to load My Todo", e);
      setRowData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };
  // ✅ RESET TO FULL LIST (from cache or API)
  const handleReset = async () => {
    setViewMode("all"); // ⭐ important
    setLoading(true);
    setSearchQuery("");

    try {
      const cached = await localforage.getItem<{
        rows: IncidentData[];
        ts: number;
      }>(CACHE_KEY);

      if (cached?.rows?.length) {
        setRowData(cached.rows);
        setFilteredData(cached.rows);
      } else {
        window.location.reload();
      }
    } catch (e) {
      console.error("Reset failed, reloading page", e);
      window.location.reload();
    } finally {
      setLoading(false);
    }
  };
  const loadMyGroupTodo = async () => {
    if (!userTeam) return;

    setViewMode("group");
    setLoading(true);

    try {
      const res = await authFetch(
          `${config.apiBaseUrl}/api/incidents/my-group-todo/${encodeURIComponent(userTeam)}`,
      );

      const data: IncidentData[] = await res.json();

      setRowData(data || []);
      setFilteredData(data || []);
    } catch (e) {
      console.error("Failed to load Group Todo", e);
      setRowData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };
  // -------------------- Effects --------------------
  useEffect(() => {
    const updatedIncident = (location.state as any)?.updatedIncident as
        | IncidentData
        | undefined;

    if (!updatedIncident) return;

    // 1. update UI rows
    // An empty list means the full list hasn't loaded yet (cache cleared);
    // seeding it with this one row would stop the full fetch from running.
    setRowData((prev) => {
      if (!prev.length) return prev;
      const exists = prev.some(
          (inc) => inc.ticketId.toString() === updatedIncident.ticketId.toString()
      );
      return exists
          ? prev.map((inc) =>
              inc.ticketId.toString() === updatedIncident.ticketId.toString()
                  ? updatedIncident
                  : inc
          )
          : [updatedIncident, ...prev];
    });

    setFilteredData((prev) => {
      if (!prev.length) return prev;
      const exists = prev.some(
          (inc) => inc.ticketId.toString() === updatedIncident.ticketId.toString()
      );
      return exists
          ? prev.map((inc) =>
              inc.ticketId.toString() === updatedIncident.ticketId.toString()
                  ? updatedIncident
                  : inc
          )
          : [updatedIncident, ...prev];
    });

    // 2. Persist into localforage
    type CacheData = {
      rows: IncidentData[];
      ts: number;
    } | null;

    localforage.getItem<CacheData>(CACHE_KEY).then((cached) => {
      // No cache: leave it for the full fetch to write, not a 1-row list
      if (!cached?.rows?.length) return;
      const rows = cached.rows;

      const exists = rows.some(
          (inc) => inc.ticketId.toString() === updatedIncident.ticketId.toString()
      );

      const updatedRows = exists
          ? rows.map((inc) =>
              inc.ticketId.toString() === updatedIncident.ticketId.toString()
                  ? updatedIncident
                  : inc
          )
          : [updatedIncident, ...rows];

      localforage.setItem(CACHE_KEY, {
        rows: updatedRows,
        ts: Date.now(),
      });
    });

    // remove state so reload doesn't re-trigger
    window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    const loadCache = async () => {
      try {
        const cached = await localforage.getItem<{
          rows: IncidentData[];
          ts: number;
        }>(CACHE_KEY);
        if (cached?.rows?.length) {
          setRowData(cached.rows);
          setFilteredData(cached.rows);
        }
      } catch (err) {
        console.error("Failed to load from IndexedDB:", err);
      } finally {
        setHydrated(true);
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(loadCache, 1000);
    return () => clearTimeout(timeoutId);
  }, [CACHE_KEY]);

  useEffect(() => {
    if (!hydrated || rowData.length > 0 || viewMode !== "all") return;

    const fetchAllIncidents = async () => {
      setLoading(true);
      const BATCH_SIZE = 2000;
      //const DELAY_MS = 500;
      let offset = 0;
      let hasMore = true;
      let allRows: IncidentData[] = [];
      const userMap = new Map<string, string>();

      try {
        while (hasMore) {
          const url =
              isAdmin && (isForTelebras || isForPetrobras || isForNilesat)
                  ? `${config.apiBaseUrl}/api/incidents/telebras/getAllIncidents/${userId}?offset=${offset}&limit=${BATCH_SIZE}`
                  : isAdmin
                      ? `${config.apiBaseUrl}/api/incidents/getAllIncidents?offset=${offset}&limit=${BATCH_SIZE}`
                      : `${config.apiBaseUrl}/api/incidents/user/${userId}/getAllIncidents?offset=${offset}&limit=${BATCH_SIZE}`;

          const response = await authFetch(url);
          if (!response.ok) throw new Error("Failed to fetch batch");

          const data: Page<IncidentData> = await response.json();
          const batch: IncidentData[] = data.content;

          if (batch.length === 0 || batch.length < BATCH_SIZE) hasMore = false;

          const newUserIds = Array.from(
              new Set(
                  batch
                      .map((incident) => incident.userid)
                      .filter((id) => !userMap.has(id))
              )
          );

          await Promise.all(
              newUserIds.map(async (id) => {
                try {
                  if (id != null) {
                    const res = await authFetch(
                        `${config.apiBaseUrl}/api/users/${id}`
                    );
                    if (!res.ok) throw new Error("User not found");
                    const user = await res.json();
                    userMap.set(id, user.username || "");
                  }
                } catch {
                  userMap.set(id, "");
                }
              })
          );

          const formattedBatch = batch.map((item) => ({
            ...item,
            responseTimeToOpen: formatDuration(item.responseTimeToOpen),
            responseTimeToClose: formatDuration(item.responseTimeToClose),
            incidentResolutionTime: formatDuration(item.incidentResolutionTime),
            user: userMap.get(item.userid) || "",
          }));

          allRows = [...allRows, ...formattedBatch];
          setRowData((prev) => [...prev, ...formattedBatch]);
          setFilteredData((prev) => [...prev, ...formattedBatch]);
          setLoading(false);


          offset += BATCH_SIZE;
        }
// Write the cache once, after every batch has arrived. Writing it per
// batch re-serialised the whole accumulated array to IndexedDB each
// time, which blocks the main thread for longer and longer as the set
// grows (2k rows, then 4k, then 6k ... ~600k row writes overall).
        await localforage.setItem(CACHE_KEY, {
          rows: allRows,
          ts: Date.now(),
        });
      } catch (err) {
        console.error("Error fetching incidents:", err);
        setLoading(false);
      }
    };
    
    fetchAllIncidents();
  }, [hydrated, rowData.length, isAdmin, userId, CACHE_KEY]);

  // -------------------- Handlers --------------------
  const handleEdit = (data: IncidentData) => {
    navigate(`/incident-edit/${data.ticketId}`, { state: { incident: data } });
  };

  const refreshDurations = (rows: IncidentData[]): IncidentData[] => {
    return rows.map((item) => ({
      ...item,
      responseTimeToOpen: formatDuration(item.responseTimeToOpen),
      responseTimeToClose: formatDuration(item.responseTimeToClose),
      incidentResolutionTime: formatDuration(item.incidentResolutionTime),
    }));
  };

  const handleDelete = async (ticketId: string) => {
    if (!window.confirm("Are you sure you want to delete this incident?"))
      return;

    try {
      const response = await authFetch(
          `${config.apiBaseUrl}/api/incidents/${ticketId}`,
          {
            method: "DELETE",
          }
      );

      if (response.ok) {
        const updatedData = rowData.filter(
            (incident) => incident.ticketId !== ticketId
        );
        setRowData(updatedData);
        setFilteredData(updatedData.filter(applySearchFilter));
      } else {
        console.error("Failed to delete incident");
      }
    } catch (error) {
      console.error("Error deleting incident:", error);
    }
  };

  const handleRowDoubleClick = async (data: IncidentData) => {
    try {
      // 1️⃣ Check lock status from backend (REAL TIME)
      // const res = await authFetch(
      //   `${config.apiBaseUrl}/api/incidents/${data.ticketId}/lock-status?userId=${userId}`
      // );

      // const isLocked = await res.json();

      // if (isLocked) {
      //   alert(`This ticket is locked by another user`);
      //   return;
      // }

      // 2️⃣ Try to lock
      // await authFetch(
      //   `${config.apiBaseUrl}/api/incidents/${data.ticketId}/lock?userId=${userId}`,
      //   { method: "POST" }
      // );

      // 3️⃣ Open modal
      setSelectedIncident(data);
      setIsModalOpen(true);
    } catch (err) {
      alert("Ticket locked by someone else");
    }
  };
  const closeModal = () => {
    if (selectedIncident) {
      authFetch(
          `${config.apiBaseUrl}/api/incidents/${selectedIncident.ticketId}/unlock?userId=${userId}`,
          { method: "POST" },
      ).catch(() => {});
    }

    setIsModalOpen(false);
    setSelectedIncident(null);
  };


  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setFilteredData(rowData.filter(applySearchFilter));
  };
  useEffect(() => {
    if (!gridApiRef.current) return;

    const api = gridApiRef.current;

    const handleFilterChanged = () => {
      const filterModel = api.getFilterModel();
      localforage.setItem(FILTER_KEY, filterModel).catch(console.error);
    };

    api.addEventListener("filterChanged", handleFilterChanged);

    return () => {
      api.removeEventListener("filterChanged", handleFilterChanged);
    };
  }, [gridApiRef.current]);
  const handleExportCSV = () => {
    if (!gridApiRef.current) return;

    const filteredNodes: IncidentData[] = [];
    gridApiRef.current.forEachNodeAfterFilter((node) => {
      if (node.data) filteredNodes.push(node.data);
    });
    if (!filteredNodes.length) return;

    const exportableColumns = columnDefs.filter((col) => !!col.field);
    const fields = exportableColumns.map(
        (col) => col.field as keyof IncidentData,
    );

    // Proper CSV field escaping (RFC 4180): double internal quotes,
    // wrap in quotes if it contains a comma, quote, or newline.
    const csvEscape = (value: unknown): string => {
      const str = String(value ?? "");
      if (/[",\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

const sanitizeField = (value: any, field: string): string => {
    if (field === "totalPausedDurationSeconds") {
      return `${Math.floor((Number(value) || 0) / 60)}m`;
      }
  if (value == null) return "";

  let str = String(value);

  // Remove HTML tags
  str = str.replace(/<[^>]*>/g, "");

  // Decode HTML entities such as &nbsp;, &amp;, &quot;, etc.
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  str = textarea.value;

  // Convert non-breaking spaces to normal spaces
  str = str.replace(/\u00A0/g, " ");

  // Clean excessive whitespace
  str = str.replace(/\s+/g, " ").trim();

  // Format ISO dates
  if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(str)) {
    str = str.replace("T", " ").split(".")[0];
  }

  return str;
};

    const headers = exportableColumns.map((col) =>
            csvEscape(
            col.field === "totalPausedDurationSeconds"
                ? "Paused Time (min)"
                : col.headerName || col.field,
        ),
    );

    const csvRows = [
      headers.join(","),
      ...filteredNodes.map((row) =>
          fields
              .map((field) => csvEscape(sanitizeField(row[field], field)))
              .join(","),
      ),
    ];

    // Prepend UTF-8 BOM so Excel renders accented/special characters correctly.
    const csvContent = "\uFEFF" + csvRows.join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "incidents.csv";
    a.click();
    URL.revokeObjectURL(url);
  };
  const columnDefs = getColumnDefs(handleEdit, handleDelete);

  // -------------------- Render --------------------
  return (
      <>
        <div className="top">
          <div className="add">
            <div className="right-section">
              <Button
                  variant="primary"
                  size="md"
                  onClick={() => navigate("/incident-add")}
              >
                + Add
              </Button>
              <Button variant="primary" size="md" onClick={handleExportCSV}>
                Export to CSV
              </Button>
              <Button variant="primary" size="md" onClick={loadMyTodo}>
                My To-Do List
              </Button>

              <Button variant="primary" size="md" onClick={loadMyGroupTodo}>
                My Group To-Do List
              </Button>
              <Button variant="primary" size="md" onClick={handleReset}>
                Reset
              </Button>
              <input
                  type="file"
                  id="excelFileInput"
                  accept=".xlsx, .xls"
                  style={{ display: "none" }}
                  onChange={handleExcelUpload}
              />
              <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  style={{
                    padding: "8px",
                    borderRadius: "5px",
                    border: "1px solid gray",
                    width: "400px",
                  }}
              />
            </div>
          </div>
        </div>

        <div className="incident-view-container">
          {/* Grid section */}
          <div
              className="grid-container"
              style={{ flex: isModalOpen ? "0 0 60%" : "1" }}
          >
            {loading ? (
                <LoadingSpinner />
            ) : (
                <div className="Grid" style={{ height: "82%" }}>
                  <Grid<IncidentData>
                      rowData={refreshDurations(filteredData)}
                      columnDefs={columnDefs}
                      onRowClick={handleRowDoubleClick}
                      onGridReady={(params: { api: GridApi<any> | null }) => {
                        gridApiRef.current = params.api;
                        setRowData((prev) => refreshDurations(prev));
                        setFilteredData((prev) => refreshDurations(prev));

                        // 🔹 Restore filters if available
                        localforage.getItem<any>(FILTER_KEY).then((savedFilter) => {
                          if (savedFilter && params.api) {
                            params.api.setFilterModel(savedFilter);
                          }
                        });
                      }}
                  />
                </div>
            )}
          </div>

          {/* Side modal */}
          {isModalOpen && (
              <div className="side-modal">
                <Modal
                    isOpen={isModalOpen}
                    data={selectedIncident}
                    onClose={closeModal}
                    onUpdate={(updated) => {
                      setTimeout(() => {
                        localforage
                            .getItem<{ rows: IncidentData[]; ts: number }>(CACHE_KEY)
                            .then((cached) => {
                              const updatedRows = cached?.rows?.map((inc) =>
                                  inc.ticketId.toString() === updated.ticketId.toString()
                                      ? updated
                                      : inc
                              ) || [updated];

                              return localforage.setItem(CACHE_KEY, {
                                rows: updatedRows,
                                ts: Date.now(),
                              });
                            })
                            .then(() =>
                                console.log("localforage updated with updatedIncident!")
                            )
                            .catch(console.error);
                      }, 0);
                      setRowData((prev) =>
                          prev.map((inc) =>
                              inc.ticketId === updated.ticketId ? updated : inc
                          )
                      );
                      setFilteredData((prev) =>
                          prev.map((inc) =>
                              inc.ticketId === updated.ticketId ? updated : inc
                          )
                      );
                    }}
                />
              </div>
          )}
        </div>
      </>
  );
};

export default IncidentView;

